import { differenceInDays, format, parseISO } from 'date-fns';
import { getOpenModal, openSignUpModal } from './modal';
import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';
import { HYDRATE_CLIENT_STATE } from './client';
import { LOGIN_SUCCESS } from './login';
import { modalTypes } from '@/enums';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';

/* Action Types */
export const AUTO_POP_MODAL_SUCCESS = 'la/ui/autoPopModal/SUCCESS';
export const DAYS_TO_WAIT_TO_SHOW_MODAL_AGAIN = 7;

// reducer
const DEFAULT_STATE = {
    dateLastShown: '',
    disableModalForTesting: false,
    hasLoggedInBefore: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case AUTO_POP_MODAL_SUCCESS:
            return {
                ...state,
                dateLastShown: action.payload,
            };
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            return {
                ...state,
                hasLoggedInBefore: true,
            };
        case HYDRATE_CLIENT_STATE:
            return {
                ...state,
                dateLastShown: action.payload.dateLastShown,
                disableModalForTesting: action.payload.disableModalForTesting,
                hasLoggedInBefore: action.payload.hasLoggedInBefore,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.autoPopModal;

export const getUiDateLastShown = createSelector(stateSelector, (state) => state.dateLastShown);
export const getUiHasLoggedInBefore = createSelector(stateSelector, (state) => state.hasLoggedInBefore);
export const getUiModalShown = createSelector(getUiDateLastShown, (date) => Boolean(date));
export const shouldStopAutoPopup = createSelector(stateSelector, (state) => state.disableModalForTesting);

export const shouldPopModal = createSelector(
    [shouldStopAutoPopup, getOpenModal, getUiDateLastShown, getUiModalShown, getUiHasLoggedInBefore],
    (abort, openModal, dateLastShown, hasSeenModal, hasLoggedInBefore) => {
        if (abort) {
            return false;
        }

        let notSeenRecently = false;
        if (Boolean(dateLastShown)) {
            const parsedDateLastShown = parseISO(dateLastShown);
            const difference = differenceInDays(new Date(), parsedDateLastShown);
            notSeenRecently = difference >= DAYS_TO_WAIT_TO_SHOW_MODAL_AGAIN;
        }
        const popModal = (!hasSeenModal || notSeenRecently) && !hasLoggedInBefore && openModal === modalTypes.NONE;
        return popModal;
    }
);

/* ACTION CREATORS */
export const autoOpenSignUpModal = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldPopModal(state)) {
        return Promise.all([
            dispatch({
                payload: format(new Date(), 'yyyy-MM-dd'),
                type: AUTO_POP_MODAL_SUCCESS,
            }),
            dispatch(openSignUpModal()),
        ]);
    } else {
        return Promise.resolve({});
    }
};
